import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { RUNTIME_CONFIG } from '@/runtime-config'

const KC = RUNTIME_CONFIG.KEYCLOAK_URL
const REALM = RUNTIME_CONFIG.KEYCLOAK_REALM
const CID = RUNTIME_CONFIG.KEYCLOAK_CLIENT_ID
const RURI = window.location.origin + '/login'

interface TS { accessToken: string; refreshToken: string; expiresAt: number }

export const useAuthStore = defineStore('auth', () => {
  const ts = ref<TS | null>(_load())
  const userInfo = ref<Record<string, unknown> | null>(null)
  const isAuthenticated = computed(() => !!ts.value && ts.value.expiresAt > Date.now())

  async function initSession() {
    if (!ts.value) return
    if (ts.value.expiresAt < Date.now() + 60_000) await _ref()
    if (ts.value && !userInfo.value) await _ui()
  }

  async function getAccessToken(): Promise<string | null> {
    if (!ts.value) return null
    if (ts.value.expiresAt < Date.now() + 60_000) await _ref()
    return ts.value?.accessToken ?? null
  }

  async function _ref() {
    if (!ts.value?.refreshToken) { logout(); return }
    try {
      const p = new URLSearchParams({ grant_type: 'refresh_token', client_id: CID, refresh_token: ts.value.refreshToken })
      const r = await fetch(`${KC}/realms/${REALM}/protocol/openid-connect/token`, { method: 'POST', body: p })
      if (!r.ok) throw new Error()
      _set(await r.json())
    } catch { logout() }
  }

  async function _ui() {
    const t2 = await getAccessToken()
    if (!t2) return
    const r = await fetch(`${KC}/realms/${REALM}/protocol/openid-connect/userinfo`, { headers: { Authorization: `Bearer ${t2}` } })
    if (r.ok) userInfo.value = await r.json()
  }

  function redirectToLogin() {
    const state = crypto.randomUUID()
    sessionStorage.setItem('oauth_state', state)
    const u = new URL(`${KC}/realms/${REALM}/protocol/openid-connect/auth`)
    u.searchParams.set('response_type', 'code')
    u.searchParams.set('client_id', CID)
    u.searchParams.set('redirect_uri', RURI)
    u.searchParams.set('scope', 'openid profile email')
    u.searchParams.set('state', state)
    window.location.href = u.toString()
  }

  async function handleCallback(code: string, state: string) {
    if (state !== sessionStorage.getItem('oauth_state')) throw new Error()
    sessionStorage.removeItem('oauth_state')
    const p = new URLSearchParams({ grant_type: 'authorization_code', client_id: CID, redirect_uri: RURI, code })
    const r = await fetch(`${KC}/realms/${REALM}/protocol/openid-connect/token`, { method: 'POST', body: p })
    if (!r.ok) throw new Error()
    _set(await r.json())
    await _ui()
  }

  function _set(d: { access_token: string; refresh_token: string; expires_in: number }) {
    const v = { accessToken: d.access_token, refreshToken: d.refresh_token, expiresAt: Date.now() + d.expires_in * 1000 }
    ts.value = v
    localStorage.setItem('cf_admin_tokens', JSON.stringify(v))
  }

  function logout() {
    ts.value = null; userInfo.value = null; localStorage.removeItem('cf_admin_tokens')
    const u = new URL(`${KC}/realms/${REALM}/protocol/openid-connect/logout`)
    u.searchParams.set('client_id', CID)
    u.searchParams.set('post_logout_redirect_uri', window.location.origin + '/login')
    window.location.href = u.toString()
  }

  return { isAuthenticated, userInfo, initSession, getAccessToken, redirectToLogin, handleCallback, logout }
})

function _load(): TS | null {
  try { return JSON.parse(localStorage.getItem('cf_admin_tokens') ?? 'null') } catch { return null }
}
